import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Edit3, X, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
// Native debounce implementation to avoid lodash-es dependency
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

interface OptimizedEditableCriterionCardProps {
  criterion: {
    id: string;
    name: string;
    description: string;
    category?: string;
    isAIGenerated?: boolean;
    isNewlyGenerated?: boolean; // Nouveau critère généré à la demande
    isCompliant?: boolean;
    validationStatus?: 'validated' | 'validating' | 'error' | 'pending';
    validationMessage?: string;
  };
  index: number;
  onUpdate: (index: number, updatedCriterion: any) => void;
  onRemove: (index: number) => void;
  onSaveDraft?: () => void; // Fonction de sauvegarde automatique
  canRemove?: boolean;
}

export const OptimizedEditableCriterionCard = ({
  criterion,
  index,
  onUpdate,
  onRemove,
  onSaveDraft,
  canRemove = true
}: OptimizedEditableCriterionCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(criterion.name);
  const [editedDescription, setEditedDescription] = useState(criterion.description);
  const [validationStatus, setValidationStatus] = useState(criterion.validationStatus || 'validated');
  const [validationMessage, setValidationMessage] = useState(criterion.validationMessage || '');
  const { toast } = useToast();

  // Debounced validation function
  const debouncedValidateCompliance = debounce(async (name: string, description: string) => {
    if (!name.trim() || !description.trim()) return;

    setValidationStatus('validating');

    try {
      const { data, error } = await supabase.functions.invoke('validate-criterion-compliance', {
        body: {
          name: name.trim(),
          description: description.trim()
        }
      });

      if (error) throw error;

      const isCompliant = data.isCompliant;
      const message = data.reason || data.suggestions?.join(' ') || '';

      setValidationStatus(isCompliant ? 'validated' : 'error');
      setValidationMessage(message);

      // Update parent with validation results
      onUpdate(index, {
        ...criterion,
        name: name.trim(),
        description: description.trim(),
        isCompliant,
        validationStatus: isCompliant ? 'validated' : 'error',
        validationMessage: message
      });

      // Toast supprimé - l'indication visuelle rouge suffit
    } catch (_error) {
      console.error('Erreur validation conformité:', _error);
      setValidationStatus('error');
      setValidationMessage('Erreur lors de la validation');
      toast({
        title: "Erreur de validation",
        description: "Impossible de valider la conformité légale. Veuillez réessayer.",
        variant: "destructive",
      });
    }
  }, 1500); // Attendre 1.5s après la dernière modification

  // Validate when editing content changes
  useEffect(() => {
    if (isEditing && (editedName !== criterion.name || editedDescription !== criterion.description)) {
      debouncedValidateCompliance(editedName, editedDescription);
    }
  }, [editedName, editedDescription, isEditing, criterion.name, criterion.description, debouncedValidateCompliance]);

  const handleSave = () => {
    if (!editedName.trim() || !editedDescription.trim()) return;

    // Only save if validation passed
    if (validationStatus === 'validated') {
      onUpdate(index, {
        ...criterion,
        name: editedName.trim(),
        description: editedDescription.trim(),
        isCompliant: true,
        validationStatus: 'validated',
        validationMessage: ''
      });
      setIsEditing(false);
      
      // Sauvegarde automatique après modification
      if (onSaveDraft) {
        // Délai court pour permettre la mise à jour du formulaire
        setTimeout(() => {
          onSaveDraft();
        }, 100);
      }
    }
  };

  const handleCancel = () => {
    setEditedName(criterion.name);
    setEditedDescription(criterion.description);
    setValidationStatus(criterion.validationStatus || 'validated');
    setValidationMessage(criterion.validationMessage || '');
    setIsEditing(false);
  };

  // Removed unused helper functions - getStatusIcon and getStatusColor

  if (isEditing) {
    return (
      <div className="p-3 bg-gray-50 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex flex-col gap-4">
        <div className="text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] leading-tight">
          Modification en cours
        </div>

        {/* Nom du critère */}
        <div className="flex flex-col gap-2">
          <div className="text-gray-800 text-xs font-medium font-['Bricolage_Grotesque']">
            Nom du critère
          </div>
          <div className="flex flex-col items-end gap-1">
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              placeholder="Nom du critère"
              maxLength={80}
              className="h-11 px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 text-xs font-medium font-['Bricolage_Grotesque'] text-gray-800 focus:outline-teal-400 border-0"
            />
            <div className="text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
              {editedName.length}/80 caractères
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="h-36 flex flex-col gap-2">
          <div className="text-gray-800 text-xs font-medium font-['Bricolage_Grotesque']">
            Description
          </div>
          <div className="flex-1 flex flex-col items-end gap-1">
            <Textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              placeholder="Description précise du critère d'évaluation"
              maxLength={300}
              rows={3}
              className="flex-1 px-3 py-3.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 text-xs font-medium font-['Bricolage_Grotesque'] text-gray-800 resize-none focus:outline-teal-400 border-0"
            />
            <div className="text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
              {editedDescription.length}/300 caractères
            </div>
          </div>
        </div>

        {/* Message d'erreur de validation */}
        {validationMessage && validationStatus === 'error' && (
          <div className="px-3 py-2.5 bg-rose-50 rounded-md outline outline-1 outline-offset-[-1px] outline-rose-500 flex flex-col gap-1">
            <div className="text-rose-500 text-xs font-medium font-['Bricolage_Grotesque'] leading-tight">
              Non-conformité détectée
            </div>
            <div className="text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque'] leading-relaxed">
              {validationMessage}
            </div>
          </div>
        )}

        {/* Indicateur de validation en cours */}
        {validationStatus === 'validating' && (
          <div className="px-2 py-1.5 bg-blue-50 rounded-md flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            <span className="text-xs font-normal font-['Bricolage_Grotesque'] text-blue-600">
              Validation de la conformité légale en cours...
            </span>
          </div>
        )}

        {/* Boutons */}
        <div className="flex justify-start items-center gap-3">
          <button
            onClick={handleCancel}
            className="px-3 py-1.5 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex items-center gap-1 hover:bg-gray-50 transition-colors"
          >
            <div className="text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque']">
              Annuler
            </div>
          </button>
          <button
            onClick={handleSave}
            disabled={
              !editedName.trim() ||
              !editedDescription.trim() ||
              validationStatus !== 'validated'
            }
            className="px-3 py-1.5 bg-teal-400 rounded-[10px] flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-teal-500 transition-colors"
          >
            <div className="text-white text-xs font-medium font-['Bricolage_Grotesque']">
              Sauvegarder
            </div>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex items-start gap-3">
      <div className={`w-9 h-9 p-2 rounded-lg flex items-center justify-center flex-shrink-0 ${
        criterion.isNewlyGenerated ? 'bg-blue-500/10' : 'bg-green-500/10'
      }`}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.44 12.076C4.24539 12.0117 4.0441 11.9698 3.84 11.951L3.699 11.945C2.761 11.945 2 12.728 2 13.693C2 14.567 2.623 15.291 3.437 15.421C3.465 15.4343 3.51067 15.4633 3.574 15.508C3.844 15.703 4.434 16.245 5.197 17.619C5.495 18.157 6.048 18.492 6.65 18.499C7.05794 18.5037 7.45349 18.3589 7.762 18.092M15 5.5C13.65 6.015 12.378 6.989 11.277 8.03C10.893 8.393 10.517 8.776 10.154 9.169M21.897 6.63C22.217 7.528 21.767 8.143 20.899 8.748C20.197 9.236 19.304 9.765 18.357 10.67C17.429 11.557 16.523 12.625 15.718 13.676C14.7332 14.9488 13.8281 16.2814 13.008 17.666C12.8629 17.921 12.6524 18.1328 12.3982 18.2793C12.1441 18.4259 11.8554 18.5021 11.562 18.5C11.2683 18.4937 10.9816 18.4095 10.7311 18.2561C10.4805 18.1028 10.2752 17.8857 10.136 17.627C9.388 16.264 8.81 15.726 8.544 15.533C7.807 14.996 7 14.903 7 13.733C7 12.776 7.746 12 8.667 12C9.325 12.027 9.929 12.309 10.456 12.693C10.798 12.942 11.161 13.271 11.538 13.705C11.98 13.051 12.513 12.297 13.111 11.516C13.979 10.383 15.003 9.166 16.101 8.117C17.181 7.085 18.431 6.119 19.754 5.609C20.617 5.276 21.576 5.732 21.897 6.63Z" stroke={criterion.isNewlyGenerated ? "#3B82F6" : "#32CD32"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="flex-1 space-y-1">
        <div className="flex justify-between items-start">
          <div className="text-gray-800 text-sm font-medium font-['Bricolage_Grotesque']">
            {criterion.name}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="text-gray-400 hover:text-teal-400 transition-colors"
              title="Modifier le critère"
            >
              <Edit3 className="h-4 w-4" />
            </button>
            {canRemove && (
              <button
                onClick={() => onRemove(index)}
                className="text-gray-400 hover:text-red-500 transition-colors"
                title="Supprimer le critère"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <p className="text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque']">
          {criterion.description}
        </p>
        {validationMessage && validationStatus === 'error' && (
          <div className="px-3 py-2.5 bg-rose-50 rounded-md outline outline-1 outline-offset-[-1px] outline-rose-500 mt-2">
            <div className="text-rose-500 text-xs font-medium font-['Bricolage_Grotesque']">
              ⚠️ {validationMessage}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};